import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../api/client';

// UI Settings Store (persisted to localStorage)
export const useUIStore = create(
  persist(
    (set) => ({
      fontSize: 'medium', // 'small', 'medium', 'large'
      setFontSize: (size) => set({ fontSize: size }),
    }),
    {
      name: 'tpccs-ui-settings',
    }
  )
);

export const useAuthStore = create((set, get) => ({
  user: null,
  isLoading: true,
  error: null,

  // Check if user is authenticated
  checkAuth: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get('/api/auth/me');
      set({ user: response.data, isLoading: false });
    } catch (error) {
      set({ user: null, isLoading: false });
    }
  },

  // Login
  login: async (username, password) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.post('/api/auth/login', { username, password });
      set({ user: response.data.user, isLoading: false });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  // Logout
  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      set({ user: null, error: null });
    }
  },

  // Update language preference
  updateLanguage: async (language) => {
    try {
      await api.put('/api/auth/language', { language });
      set(state => ({
        user: state.user ? { ...state.user, preferredLanguage: language } : null,
      }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error };
    }
  },

  // Change password
  changePassword: async (currentPassword, newPassword) => {
    try {
      await api.put('/api/auth/change-password', { currentPassword, newPassword });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error };
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  // Check if user has specific role
  hasRole: (role) => {
    const { user } = get();
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  },

  // Check if user is admin
  isAdmin: () => get().user?.role === 'admin',

  // 系統管理員 / 處長室 / 工安組: all divisions
  hasFullAccess: () => Boolean(get().user?.permissions?.fullAccess),

  // 承攬商 account
  isContractor: () => Boolean(get().user?.permissions?.isContractor),

  // Check if user can send LINE messages (manual push)
  canSendLine: () => Boolean(get().user?.permissions?.canPush),

  // Check if user can manage push time / holiday settings
  canManageSettings: () => Boolean(get().user?.permissions?.canManageSettings),

  // Divisions the user may create reports for
  creatableDivisions: () => get().user?.permissions?.creatableDivisions || [],
}));

export default useAuthStore;
